import { useEffect, useRef, useState } from "react";

export type ReportConfigSelectOption<T extends string> = { value: T; label: string; disabled?: boolean };

interface ReportConfigSelectProps<T extends string> {
    value: T;
    options: ReportConfigSelectOption<T>[];
    onChange: (value: T) => void;
    ariaLabel: string;
    disabled?: boolean;
}

export function ReportConfigSelect<T extends string>({ value, options, onChange, ariaLabel, disabled = false }: ReportConfigSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const selected = options.find((option) => option.value === value) || options[0];

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    return (
        <div className={[open ? "rc-config-select open" : "rc-config-select", disabled ? "disabled" : ""].filter(Boolean).join(" ")} ref={rootRef}>
            <button
                type="button"
                className="rc-config-select-trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                title={selected?.label || ariaLabel}
                disabled={disabled}
                onClick={() => {
                    if (disabled) return;
                    setOpen((current) => !current);
                }}
                onKeyDown={(event) => {
                    if (event.key === "Escape") setOpen(false);
                }}
            >
                <span>{selected?.label || "Select..."}</span>
                <span className="material-icons md-16" aria-hidden="true">expand_more</span>
            </button>
            {open && (
                <div className="rc-config-select-menu" role="listbox" aria-label={ariaLabel}>
                    {options.map((option) => {
                        const isSelected = option.value === value;
                        const isDisabled = !!option.disabled;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={["rc-config-select-option", isSelected ? "selected" : "", isDisabled ? "disabled" : ""].filter(Boolean).join(" ")}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={isDisabled}
                                disabled={isDisabled}
                                onClick={() => {
                                    if (isDisabled) return;
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
